import { DistributionChannel, Team } from '../../api/queries';
import i18n from '../../i18n/config';
import { SurveyFrequency } from '../../api/types/survey-enums';

import {
  Step,
  SurveyFormData,
} from '../../components/SurveyFormCard/SurveyFormCard';

export enum SurveyFormCardStepsEnum {
  surveySetup = 'surveySetup',
  surveyDistributionChannels = 'surveyDistributionChannels',
  surveyTimeline = 'surveyTimeline',
  surveySummary = 'surveySummary',
}

type SurveyFormField = keyof SurveyFormData;

/** Fields validated when leaving each step, in step order. */
export const STEP_FIELDS: Record<SurveyFormCardStepsEnum, SurveyFormField[]> = {
  [SurveyFormCardStepsEnum.surveySetup]: [
    'team',
    'surveyType',
    'psychologicalSafetyTemplateId',
    'workEnvironmentTemplateId',
    'customTemplateId',
    'name',
  ],
  [SurveyFormCardStepsEnum.surveyDistributionChannels]: [],
  [SurveyFormCardStepsEnum.surveyTimeline]: [
    'date',
    'time',
    'deadlineInDays',
    'daysBeforeDeadlineToNotify',
    'actionProposalDeadlineInDays',
    'actionVotingDeadlineInDays',
    'frequency',
  ],
  [SurveyFormCardStepsEnum.surveySummary]: [],
};

/** DOM ids of the inputs for each field, used by the error summary links. */
export const FIELD_IDS: Partial<Record<SurveyFormField, string>> = {
  team: 'choose-team',
  surveyType: 'survey-type',
  psychologicalSafetyTemplateId: 'survey-template',
  workEnvironmentTemplateId: 'survey-template',
  customTemplateId: 'survey-template',
  name: 'survey-name',
  date: 'survey-date',
  time: 'survey-time',
  deadlineInDays: 'survey-deadline',
  daysBeforeDeadlineToNotify: 'survey-reminder',
  actionProposalDeadlineInDays: 'survey-action-proposal-deadline',
  actionVotingDeadlineInDays: 'survey-action-voting-deadline',
  frequency: 'survey-frequency',
};

export class SurveyFormCardUtils {
  public static SURVEY_DRAFT_KEY = 'create-survey-draft';
  public static SURVEY_STEP_KEY = 'create-survey-step';
  public static PLANNED_SURVEY_DRAFT_KEY = 'planned-survey-draft';
  public static PLANNED_SURVEY_STEP_KEY = 'planned-survey-step';

  public static get initialSteps(): Step[] {
    return this.getInitialSteps();
  }

  public static get infoTexts(): Record<SurveyFormCardStepsEnum, { id: SurveyFormCardStepsEnum; title: string; text: string }> {
    return {
      [SurveyFormCardStepsEnum.surveySetup]: this.getInfoText(SurveyFormCardStepsEnum.surveySetup),
      [SurveyFormCardStepsEnum.surveyDistributionChannels]: this.getInfoText(SurveyFormCardStepsEnum.surveyDistributionChannels),
      [SurveyFormCardStepsEnum.surveyTimeline]: this.getInfoText(SurveyFormCardStepsEnum.surveyTimeline),
      [SurveyFormCardStepsEnum.surveySummary]: this.getInfoText(SurveyFormCardStepsEnum.surveySummary),
    };
  }

  public static getFrequencyLabel(value: string): string {
    const map: Record<string, string> = {
      [SurveyFrequency.WEEKLY]: i18n.t('surveyCreation.timeline.planning.frequencies.weekly'),
      [SurveyFrequency.EVERY_OTHER_WEEK]: i18n.t('surveyCreation.timeline.planning.frequencies.everyOtherWeek'),
      [SurveyFrequency.EVERY_THIRD_WEEK]: i18n.t('surveyCreation.timeline.planning.frequencies.everyThirdWeek'),
      [SurveyFrequency.MONTHLY]: i18n.t('surveyCreation.timeline.planning.frequencies.monthly'),
      [SurveyFrequency.EVERY_OTHER_MONTH]: i18n.t('surveyCreation.timeline.planning.frequencies.everyOtherMonth'),
    };
    return map[value] || value;
  }

  public static getFrequencyOptions(): Array<{ label: string; value: string }> {
    return [
      {
        label: i18n.t('surveyCreation.timeline.planning.frequencies.weekly'),
        value: SurveyFrequency.WEEKLY,
      },
      {
        label: i18n.t('surveyCreation.timeline.planning.frequencies.everyOtherWeek'),
        value: SurveyFrequency.EVERY_OTHER_WEEK,
      },
      {
        label: i18n.t('surveyCreation.timeline.planning.frequencies.everyThirdWeek'),
        value: SurveyFrequency.EVERY_THIRD_WEEK,
      },
      {
        label: i18n.t('surveyCreation.timeline.planning.frequencies.monthly'),
        value: SurveyFrequency.MONTHLY,
      },
      {
        label: i18n.t('surveyCreation.timeline.planning.frequencies.everyOtherMonth'),
        value: SurveyFrequency.EVERY_OTHER_MONTH,
      },
    ];
  }

  public static getSurveyTypeLabel(value: SurveyFormData['surveyType']): string {
    const map: Record<SurveyFormData['surveyType'], string> = {
      'psychological-safety': i18n.t('surveyCreation.setup.surveyTypes.psychologicalSafety'),
      'work-environment': i18n.t('surveyCreation.setup.surveyTypes.workEnvironment'),
      'custom': i18n.t('surveyCreation.setup.surveyTypes.custom'),
    };
    return map[value];
  }

  public static getStepTitle(stepId: SurveyFormCardStepsEnum): string {
    const map: Record<SurveyFormCardStepsEnum, string> = {
      [SurveyFormCardStepsEnum.surveySetup]: i18n.t('surveyCreation.stepper.setup'),
      [SurveyFormCardStepsEnum.surveyDistributionChannels]: i18n.t('surveyCreation.stepper.distributionChannels'),
      [SurveyFormCardStepsEnum.surveyTimeline]: i18n.t('surveyCreation.stepper.timeline'),
      [SurveyFormCardStepsEnum.surveySummary]: i18n.t('surveyCreation.stepper.summary'),
    };
    return map[stepId];
  }

  public static getInitialSteps(): Step[] {
    return [
      {
        id: SurveyFormCardStepsEnum.surveySetup,
        title: i18n.t('surveyCreation.stepper.setup'),
        stepNumber: 1,
        completed: false,
        inProgress: true,
      },
      {
        id: SurveyFormCardStepsEnum.surveyDistributionChannels,
        title: i18n.t('surveyCreation.stepper.distributionChannels'),
        stepNumber: 2,
        completed: false,
        inProgress: false,
      },
      {
        id: SurveyFormCardStepsEnum.surveyTimeline,
        title: i18n.t('surveyCreation.stepper.timeline'),
        stepNumber: 3,
        completed: false,
        inProgress: false,
      },
      {
        id: SurveyFormCardStepsEnum.surveySummary,
        title: i18n.t('surveyCreation.stepper.summary'),
        stepNumber: 4,
        completed: false,
        inProgress: false,
      },
    ];
  }

  public static getInfoText(stepId: SurveyFormCardStepsEnum): { id: SurveyFormCardStepsEnum; title: string; text: string } {
    const map: Record<SurveyFormCardStepsEnum, { id: SurveyFormCardStepsEnum; title: string; text: string }> = {
      [SurveyFormCardStepsEnum.surveySetup]: {
        id: SurveyFormCardStepsEnum.surveySetup,
        title: i18n.t('surveyCreation.infoText.setup.title'),
        text: i18n.t('surveyCreation.infoText.setup.text'),
      },
      [SurveyFormCardStepsEnum.surveyDistributionChannels]: {
        id: SurveyFormCardStepsEnum.surveyDistributionChannels,
        title: i18n.t('surveyCreation.infoText.distributionChannels.title'),
        text: i18n.t('surveyCreation.infoText.distributionChannels.text'),
      },
      [SurveyFormCardStepsEnum.surveyTimeline]: {
        id: SurveyFormCardStepsEnum.surveyTimeline,
        title: i18n.t('surveyCreation.infoText.timeline.title'),
        text: i18n.t('surveyCreation.infoText.timeline.text'),
      },
      [SurveyFormCardStepsEnum.surveySummary]: {
        id: SurveyFormCardStepsEnum.surveySummary,
        title: i18n.t('surveyCreation.infoText.summary.title'),
        text: i18n.t('surveyCreation.infoText.summary.text'),
      },
    };
    return map[stepId];
  }

  public static getInitialFormData = (teamId: number): SurveyFormData => {
    const stored = sessionStorage.getItem(this.SURVEY_DRAFT_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        sessionStorage.removeItem(this.SURVEY_DRAFT_KEY);
      }
    }
    return {
      team: teamId,
      name: '',
      date: '',
      time: '',
      isPlanning: false,
      isRepeating: false,
      frequency: '',
      deadlineInDays: 1,
      actionProposalDeadlineInDays: 7,
      actionVotingDeadlineInDays: 7,
      daysBeforeDeadlineToNotify: 0,
      surveyType: 'psychological-safety',
      psychologicalSafetyTemplateId: undefined,
      workEnvironmentTemplateId: undefined,
    };
  };

  public static removeSessionKeys(isEditingPlannedSurvey: boolean) {
    sessionStorage.removeItem(
      isEditingPlannedSurvey
        ? this.PLANNED_SURVEY_DRAFT_KEY
        : this.SURVEY_DRAFT_KEY,
    );
    sessionStorage.removeItem(
      isEditingPlannedSurvey
        ? this.PLANNED_SURVEY_STEP_KEY
        : this.SURVEY_STEP_KEY,
    );
  }

  public static removeAllSessionKeys() {
    sessionStorage.removeItem(this.PLANNED_SURVEY_DRAFT_KEY);
    sessionStorage.removeItem(this.SURVEY_DRAFT_KEY);
    sessionStorage.removeItem(this.PLANNED_SURVEY_STEP_KEY);
    sessionStorage.removeItem(this.SURVEY_STEP_KEY);
  }

  public static teamsToSelectableItems(teams: Array<Team>) {
    return teams.map((team) => ({
      label: team.name,
      value: team.id,
    }));
  }

  public static teamAlreadySelected = (teams: Array<Team>, teamId: number) => {
    const foundTeam = teams.find((team) => team.id === teamId);
    return foundTeam ? foundTeam.id : 0;
  };

  public static hasDistributionChannel(
    distributionChannels?: Array<DistributionChannel>,
  ) {
    return distributionChannels && distributionChannels.length > 0;
  }

  public static persistDraft = (data: SurveyFormData) => {
    sessionStorage.setItem('create-survey-draft', JSON.stringify(data));
  };
}
